import React from 'react';
import './Firstnames.css';

import 'bootstrap/dist/css/bootstrap.min.css';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import translationsJson from 'data/translations.json';

import WordCloud from 'components/Wordcloud/Wordcloud';
import MapContainer from 'components/Map/MapContainer';
import firstnameIndices from 'data/firstnameIndex.json';
import Ranking from 'components/Ranking/Ranking';
import rankingNamesJson from 'data/firstnameRanking.json';
import firstnameMunicipalityRankingJson from 'data/firstnameMunicipalityRanking.json';

import { NameType, WordCloudEntry, Sex, FirstnameRankingData, LanguageGroup, SexSelection, RankingEntry, DownloadType } from 'lib/Types';

import info from 'data/info.json';
import 'general.css';
import GenderFilter from 'components/GenderFilter/GenderFilter';
import LanguageFilter from 'components/LanguageFilter/LanguageFilter';
import MunicipalityFilter from 'components/MunicipalityFilter/MunicipalityFilter';
import municipalitiesJson from "data/municipality.json";
import DownloadSection from 'components/DownloadSection/DownloadSection';
import Annotation from 'components/Annotation/Annotation';
import Indication from 'components/Indication/Indication';
import { MobileDeviceContext } from 'lib/Screen';
import withRouter from 'lib/withRouter';
import { NavigateFunction } from 'react-router-dom';

const municipalities: any = municipalitiesJson;

const translations: Translations = translationsJson;
const rankingNames: FirstnameRankingData = rankingNamesJson;
const firstnameMunicipalityRanking: FirstnameRankingData = firstnameMunicipalityRankingJson;

enum TabNames {
	Wordcloud = 0,
	Ranking = 1
}

interface Props { 
	params: Record<string, string>;
	navigate: NavigateFunction;
}

interface State {
	tabKey: string;
	selectedName: string | undefined;
	sex: Sex | undefined;
	sexSelection: SexSelection[];
	language: LanguageGroup[];
	municipality: string;
}

class Firstnames extends React.Component<Props, State> {
	static contextType = MobileDeviceContext;

	private wordCloudNames: WordCloudEntry[];

	constructor(props: Props) {
		super(props);

		this.state = {
			tabKey: 'tab' in this.props.params ? this.props.params['tab'] : "wordcloud",
			selectedName: undefined,
			sex: undefined,
			sexSelection: [SexSelection.All, SexSelection.All],
			language: [LanguageGroup.All, LanguageGroup.All],
			municipality: Object.keys(municipalities)[0],
		};

		this.wordCloudNames = [];
		this.generateWordCloudNames(this.state.language[TabNames.Wordcloud], this.state.sexSelection[TabNames.Wordcloud])

		this.onNameSelected = this.onNameSelected.bind(this);
		this.onMunicipalitySelected = this.onMunicipalitySelected.bind(this);
		this.generateWordCloudNames = this.generateWordCloudNames.bind(this);
		this.onUpdateSelectedSex = this.onUpdateSelectedSex.bind(this);
		this.onUpdateLanguage = this.onUpdateLanguage.bind(this);
	}

	private onNameSelected(name: string, sex: Sex | undefined) {
		this.setState({ tabKey: "map", selectedName: name, sex: sex });
		this.props.navigate("map");
	}

	private onMunicipalitySelected(municipality: string) {
		this.setState({ tabKey: "rankingMunicipality", municipality: municipality });
		this.props.navigate("rankingMunicipality");
	}

	private generateWordCloudNames(language: LanguageGroup, sex: SexSelection,) {
		const nameEntryToType = (entry: RankingEntry, sex: Sex) => ({ text: entry.name, value: entry.count, sex: sex });
		this.wordCloudNames = [];
		const rankingData = rankingNames[language];

		if (sex === SexSelection.All) {
			const maleData = rankingData.m.slice(0, Math.min(rankingData.m.length, info.wordCloudNameCount / 2)).map(item => nameEntryToType(item, Sex.Male));
			const femaleData = rankingData.f.slice(0, Math.min(rankingData.f.length, info.wordCloudNameCount / 2)).map(item => nameEntryToType(item, Sex.Female));
			this.wordCloudNames = maleData.concat(femaleData);
		} else if (sex === SexSelection.Male) {
			this.wordCloudNames = rankingData.m.slice(0, Math.min(rankingData.m.length, info.wordCloudNameCount)).map(item => nameEntryToType(item, Sex.Male));
		} else {
			this.wordCloudNames = rankingData.f.slice(0, Math.min(rankingData.f.length, info.wordCloudNameCount)).map(item => nameEntryToType(item, Sex.Female));
		}
	}

	private onUpdateSelectedSex(sex: SexSelection, index: number) {
		if (index === TabNames.Wordcloud) {
			this.generateWordCloudNames(this.state.language[index], sex);
		}
		const selection = this.state.sexSelection;
        selection[index] = sex;
        this.setState({ sexSelection: selection });
	}

	private onUpdateSelectedMunicipality(municipality: string) {
		this.setState({ municipality: municipality });
	}

	private onUpdateLanguage(newLanguage: LanguageGroup, index: number) {
		if (index === TabNames.Wordcloud) {
			this.generateWordCloudNames(newLanguage, this.state.sexSelection[index]);
		}
		const language = this.state.language;
        language[index] = newLanguage;
        this.setState({ language: language });
	}

	private renderWordCloud() {
		return (
			<>
				<div className="filter-div">
					<GenderFilter onUpdate={(e) => { this.onUpdateSelectedSex(e, TabNames.Wordcloud) }} young={false} />
					<LanguageFilter onUpdate={(e) => { this.onUpdateLanguage(e, TabNames.Wordcloud) }} />
				</div>
				<WordCloud words={this.wordCloudNames} nameSelected={this.onNameSelected} />
			</>
		);
	}

	private renderMap() {
		return (
			<MapContainer nameIndices={firstnameIndices} nameType={NameType.Firstname} selectedPerson={this.state.selectedName ? { name: this.state.selectedName, sex: this.state.sex } : undefined} onClick={this.onMunicipalitySelected} />
		);
	}

	private renderRanking() {
		const rankingDataMale = rankingNames[this.state.language[TabNames.Ranking]].m.slice(0, Math.min(rankingNames[this.state.language[TabNames.Ranking]].m.length, info.firstnameRankingCount));
		const rankingDataFemale = rankingNames[this.state.language[TabNames.Ranking]].f.slice(0, Math.min(rankingNames[this.state.language[TabNames.Ranking]].f.length, info.firstnameRankingCount));

		return (
			<>
				<div className="filter-div">
					<LanguageFilter onUpdate={(e) => { this.onUpdateLanguage(e, TabNames.Ranking) }} />
				</div>
				<div className="side-by-side">
					<Ranking names={rankingDataMale} title={translations.firstnames[window.lang] + " - " + `${info.firstnamesYear}`} sex={Sex.Male} nameSelected={this.onNameSelected}></Ranking>
					<Ranking names={rankingDataFemale} title={translations.firstnames[window.lang] + " - " + `${info.firstnamesYear}`} sex={Sex.Female} nameSelected={this.onNameSelected}></Ranking>
				</div>
			</>
		);
	}

	private renderRankingMunicipality() {
		return (
			<>
				<div className="filter-div">
					<MunicipalityFilter onUpdate={(e) => { this.onUpdateSelectedMunicipality(e) }} value={this.state.municipality} />
				</div>
				<div className="side-by-side">
					<Ranking names={firstnameMunicipalityRanking[this.state.municipality].m} title={municipalities[this.state.municipality].name[window.lang] + " - " + info.firstnamesYear} sex={Sex.Male} nameSelected={this.onNameSelected}></Ranking>
					<Ranking names={firstnameMunicipalityRanking[this.state.municipality].f} title={municipalities[this.state.municipality].name[window.lang] + " - " + info.firstnamesYear} sex={Sex.Female} nameSelected={this.onNameSelected}></Ranking>
				</div>
			</>
		);
	}

	componentDidUpdate(prevProps: Props) {
		const prevTab = 'tab' in prevProps.params ? prevProps.params['tab'] : null;
		const tab = 'tab' in this.props.params ? this.props.params['tab'] : null;
		if (tab && prevTab !== tab)
			this.setState({ tabKey: tab })
	}

	render() {
		let content: JSX.Element;
		//console.log("firstname params:", this.props.params);
		if (this.context) {
			switch (this.state.tabKey) {
				case 'wordcloud':
					content = this.renderWordCloud();
					break;
				case 'map':
					content = this.renderMap();
					break;
				case 'ranking':
					content = this.renderRanking();
					break;
				case 'rankingMunicipality':
					content = this.renderRankingMunicipality();
					break;
				default:
					content = <h3>404 Not Found</h3>
					break;
			}


		} else {
			content = (
				<Tabs defaultActiveKey="wordcloud" activeKey={this.state.tabKey} onSelect={(key) => this.setState({ tabKey: key! })} className="mb-3">
					<Tab eventKey="wordcloud" title={translations.wordcloud[window.lang]}>
						{this.renderWordCloud()}
					</Tab>
					<Tab eventKey="map" title={translations.map[window.lang]}>
						{this.renderMap()}
					</Tab>
					<Tab eventKey="ranking" title={translations.ranking[window.lang]}>
						{this.renderRanking()}
					</Tab>
					<Tab eventKey="rankingMunicipality" title={translations.rankingMunicipality[window.lang]}>
						{this.renderRankingMunicipality()}
					</Tab>
				</Tabs>
			);
		}

		return (
			<>
				{content}

				<Annotation page='firstnames' tab={this.state.tabKey} />
				<DownloadSection type={DownloadType.Firstnames} />
				<Indication />
			</>
		);
	}
}

export default withRouter(Firstnames);